import mergeWith from 'lodash.mergewith';
import { checkIsObject } from '../../../../utils/helperFunctions';
import moment from 'moment/moment';

const getItemInObjectByPath = (obj, path) => {
  if (!obj || !path) {
    return undefined;
  }

  // extract path by dot or [
  const pathParts = path.split(/[.\[\]]/).filter((part) => part !== '');

  return pathParts.reduce((current, key) => {
    if (current === null || current === undefined) {
      return undefined;
    }

    // Check if this is a numeric index for an array (or obj)
    const numericKey = parseInt(key, 10);
    if (!isNaN(numericKey)) {
      return current[numericKey];
    }

    return current[key];
  }, obj);
};

// this fn for createShouldShowField
const checkValueMatch = (currentValue, allowedValues) => {
  const allowedSet = new Set(allowedValues);

  // array if a field has multi select
  if (Array.isArray(currentValue)) {
    return currentValue.some((val) => allowedSet.has(val));
  }
  // string if a field has a single select
  return allowedSet.has(currentValue);
};

// creates a function that determines whether a field should be displayed
export const createShouldShowField = (getValues, sectionName) => {
  return (item, fieldIndex = null) => {
    if (!item.condition) {
      return true;
    }

    const watchedValues = getValues();
    for (const [conditionKey, allowedValues] of Object.entries(
      item.condition
    )) {
      // Split conditionKey by "||" to handle multiple conditions
      const conditionKeys = conditionKey.split('||');

      let hasMatchingCondition = false;

      for (const singleConditionKey of conditionKeys) {
        let currentValue;
        const conditions = singleConditionKey.split('.');

        // if the first path in the condition is sectionName - this is a local condition
        const isLocalCondition = conditions[0] === sectionName;

        // clear conditions from the current section
        const conditionKeyWithoutCurrentSection = isLocalCondition
          ? conditions.slice(1).join('.')
          : singleConditionKey;

        // for global conditions
        if (!isLocalCondition) {
          currentValue = getItemInObjectByPath(
            watchedValues,
            conditionKeyWithoutCurrentSection
          );
        }

        // for multiple sections, we take the value from a specific index
        else if (fieldIndex !== null) {
          const fieldParent = watchedValues?.[sectionName]?.[fieldIndex];

          currentValue = getItemInObjectByPath(
            fieldParent,
            conditionKeyWithoutCurrentSection
          );
        } else {
          // for single non-global sections
          const fieldParent = watchedValues?.[sectionName];

          currentValue = getItemInObjectByPath(
            fieldParent,
            conditionKeyWithoutCurrentSection
          );
        }

        // if a match is found for this key, we set the flag
        if (checkValueMatch(currentValue, allowedValues)) {
          hasMatchingCondition = true;
          // exit the loop because we found a match
          break;
        }
      }

      // if none of the conditionKeys matches, return false
      if (!hasMatchingCondition) {
        return false;
      }
    }

    return true;
  };
};

// creates a function that determines whether a form block is required
export const createIsRequiredBlockCheck = (getValues, sectionName) => {
  // Block is required if it has required fields AND at least one filled field
  return (item, fieldIndex = null) => {
    // if block is necessarily required it has "required" param
    if (item.required) {
      return true;
    }

    // if it isn't, check fields into the block

    const watchedValues = getValues();

    let blockData;
    if (fieldIndex !== null) {
      // For multiple sections
      blockData = watchedValues?.[sectionName]?.[fieldIndex];
    } else {
      // For single sections
      blockData = getItemInObjectByPath(watchedValues, sectionName);
    }

    if (!blockData || !item.fields) {
      return false;
    }

    // Check if there's at least one required field in the first nested level
    const hasRequiredField = item.fields.flat().some((field) => field.required);

    // Check if there's at least one filled field in the block
    // UPD we decided to make block as required if at least one field is required!
    // if you want to get back this logic - delete one line below (return) and uncomment the next lines
    return hasRequiredField;

    // if (!hasRequiredField) {
    //   return false;
    // }
    // return item.fields.flat().some((field) => {
    //   const fieldValue = blockData[field.key];
    //   return (
    //     fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
    //   );
    // });
  };
};

//  For FormSection to unregister removed fields from the Incident form
export const createRemoveFiled = (getValues, unregister, sectionName) => {
  const watchedValues = getValues();

  return (item, fieldIndex = null) => {
    // for multiple sections
    if (fieldIndex !== null) {
      const fieldValue = watchedValues?.[sectionName]?.[fieldIndex]?.[item.key];
      if (fieldValue !== undefined) {
        unregister(`${sectionName}.${fieldIndex}.${item.key}`);
      }
    } else {
      // for single
      const fieldValue = watchedValues?.[sectionName]?.[item.key];
      if (fieldValue !== undefined) {
        unregister(`${sectionName}.${item.key}`);
      }
    }
  };
};

// helper for form validation
export function getAllErrorPaths(errors, currentPath = '') {
  const errorPaths = [];

  // Iterate through all properties in the errors object
  for (const key in errors) {
    if (!errors.hasOwnProperty(key)) continue;

    const value = errors[key];
    // Build the path by concatenating keys with dots
    const newPath = currentPath ? `${currentPath}.${key}` : key;

    // Check if this is a final error object (contains message, type, or ref properties)
    if (
      value &&
      typeof value === 'object' &&
      (value.message || value.type || value.ref)
    ) {
      errorPaths.push(newPath);
    }

    // If it's a nested object or array, continue recursively
    if (
      value &&
      typeof value === 'object' &&
      !value.message &&
      !value.type &&
      !value.ref
    ) {
      const nestedPaths = getAllErrorPaths(value, newPath);
      errorPaths.push(...nestedPaths);
    }
  }

  return errorPaths;
}

export const getSectionsByPathSuffix = (pathList, path) => {
  return pathList.filter((section) => !!section.startsWith(path));
};

export const checkIsExpandedAccordion = (pathList, path) => {
  return !!getSectionsByPathSuffix(pathList, path)?.length;
};

// helper fn for processFields
const getTransformedFieldValue = (field, dataSource) => {
  const { key, type, multiple } = field;

  const value = dataSource[key];

  if (value === undefined || value === null) {
    return null;
  }

  let transformedValue;
  if (multiple) {
    transformedValue = Array.isArray(value) ? value : [value];
  } else if (type === 'datetime' || type === 'date') {
    transformedValue = moment.utc(value, moment.ISO_8601);
  } else if (type === 'month') {
    transformedValue = moment(value, 'MM/YYYY');
  } else {
    transformedValue = value;
  }

  return { key, transformedValue };
};

// helper fn for processSection
const processFields = (fields, data) => {
  if (!fields || !data) {
    return {};
  }

  return fields.flat().reduce((acc, field) => {
    const fieldResult = getTransformedFieldValue(field, data);
    if (fieldResult) {
      acc[fieldResult.key] = fieldResult.transformedValue;
    }
    return acc;
  }, {});
};

const processSection = (sectionItem, sectionData) => {
  if (!sectionItem.name) {
    return {};
  }

  const data =
    sectionData?.[sectionItem.name] ??
    (sectionItem.label && sectionData?.[sectionItem.label]);

  if (!data) {
    // Process nested groups even if no data exists for current level
    const nestedGroups = processNestedGroups(sectionItem.groups, sectionData);
    return Object.keys(nestedGroups).length > 0
      ? { [sectionItem.name]: nestedGroups }
      : {};
  }

  if (sectionItem.single === false && Array.isArray(data)) {
    // Multi section
    const processedItems = data.map((item) => {
      const fieldValues = processFields(sectionItem.fields, item);
      const nestedGroups = processNestedGroups(sectionItem.groups, item);

      return { ...fieldValues, ...nestedGroups };
    });

    return { [sectionItem.name]: processedItems };
  } else {
    // Single section
    const fieldValues = processFields(sectionItem.fields, data);
    const nestedGroups = processNestedGroups(sectionItem.groups, data);

    const combinedValues = { ...fieldValues, ...nestedGroups };

    if (Object.keys(combinedValues).length > 0) {
      return { [sectionItem.name]: combinedValues };
    }
  }

  return {};
};

// helper fn for processSection
const processNestedGroups = (groups, data) => {
  if (!groups || typeof groups !== 'object') {
    return {};
  }

  return Object.values(groups)
    .flat()
    .reduce((acc, groupItem) => {
      const processedSection = processSection(groupItem, data);
      return { ...acc, ...processedSection };
    }, {});
};

export const extractDefaultValuesFromSettings = (settingsArray, data) => {
  if (!settingsArray || !Array.isArray(settingsArray)) {
    return {};
  }

  return settingsArray.flat().reduce((acc, section) => {
    const processedSection = processSection(section, data);
    return { ...acc, ...processedSection };
  }, {});
};

export const getActiveMenuStyles = (expandedSections, id) => {
  const match = expandedSections.at(-1)?.match(/^([^.\[]+)/); // extract section name before dot or [
  if (!match) {
    return {};
  }

  return match[1] === id
    ? {
        background: '#F3F3F3',
        fontWeight: 600,
      }
    : {};
};

// Deep merge with priority for the first object.
// Arrays from the second object are added only if the first array is empty.
export const deepMergePreferFirst = (obj1, obj2) => {
  return mergeWith({}, obj1, obj2, (objValue, srcValue) => {
    // If both values are arrays:
    if (Array.isArray(objValue) && Array.isArray(srcValue)) {
      // Keep the first array if it’s not empty, otherwise use the second one
      return objValue.length ? objValue : srcValue;
    }

    // deep merge for objects
    if (checkIsObject(objValue) && checkIsObject(srcValue)) {
      return undefined;
    }

    // If the value exists in the first object (even if null) — keep it
    if (objValue !== undefined && objValue !== null) {
      return objValue;
    }

    // If the value is missing in the first object — take it from the second
    return srcValue;
  });
};

// Function for finding a field block in form settings,
// for example, to check if a specific field exists
export const findFieldInConfig = (fieldName, settings) => {
  if (!settings || !Array.isArray(settings)) return null;

  // Use flat() like in your other functions to simplify the traversal
  for (const item of settings.flat()) {
    // Check if the current element is the searched field
    if (item.name === fieldName || item.key === fieldName) {
      return item;
    }

    // Recursion into nested groups
    if (item.groups) {
      const found = findFieldInConfig(
        fieldName,
        Object.values(item.groups).flat()
      );
      if (found) return found;
    }

    // Recursion into fields (considering that fields is an array of arrays)
    if (item.fields) {
      const found = findFieldInConfig(fieldName, item.fields.flat());
      if (found) return found;
    }
  }

  return null;
};

const toMoment = (value) =>
  moment.isMoment(value) ? value : moment.utc(value, moment.ISO_8601);

// CAD only sends per-unit clear times (unit_responses[].unit_clear) — it never
// sends an incident-level one, so dispatch.incident_clear always arrives null.
// Fall back to the latest clear time among the units that have one.
export const getLatestUnitClear = (unitResponses) => {
  if (!Array.isArray(unitResponses)) {
    return null;
  }

  const clearTimes = unitResponses
    .map((unit) => unit?.unit_clear)
    .filter(Boolean)
    .map(toMoment)
    .filter((item) => item.isValid());

  return clearTimes.length ? moment.max(clearTimes) : null;
};

// Prefills dispatch.incident_clear when the server has no value for it.
// Never overwrites a value that already exists (from the server or the user).
export const withIncidentClearFallback = (values, data, settings) => {
  if (values?.dispatch?.incident_clear) {
    return values;
  }

  // only prefill if the form actually renders this field
  if (!findFieldInConfig('incident_clear', settings)) {
    return values;
  }

  const latestUnitClear = getLatestUnitClear(data?.unit_responses);
  if (!latestUnitClear) {
    return values;
  }

  return {
    ...values,
    dispatch: { ...values.dispatch, incident_clear: latestUnitClear },
  };
};
